import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYSIS_NAME_EXISTS_ERROR } from '../../../services/api/companyAnalysisService';

interface SaveAnalysisModalProps {
    isOpen: boolean;
    onSave: (name: string) => Promise<boolean>;
    onClose: () => void;
    defaultName?: string;
}

/**
 * Modal pour nommer et sauvegarder une analyse
 */
export const SaveAnalysisModal: React.FC<SaveAnalysisModalProps> = ({
    isOpen,
    onSave,
    onClose,
    defaultName = '',
}) => {
    const { t } = useTranslation();
    const [analysisName, setAnalysisName] = useState(defaultName);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Mettre à jour le nom quand le modal s'ouvre ou que defaultName change
    useEffect(() => {
        if (isOpen) {
            setAnalysisName(defaultName);
            setError('');
        }
    }, [isOpen, defaultName]);

    const handleSave = async () => {
        if (!analysisName.trim()) {
            setError(t('features.swimlane.analysisNameRequired') || 'Le nom de l\'analyse est requis');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const success = await onSave(analysisName.trim());
            if (success) {
                setAnalysisName('');
                onClose();
            } else {
                setError(t('features.swimlane.errorSavingAnalysis') || 'Erreur lors de la sauvegarde');
            }
        } catch (err: any) {
            const message = err?.message === ANALYSIS_NAME_EXISTS_ERROR
                ? (t('features.swimlane.analysisNameAlreadyExists') || 'Une analyse avec ce nom existe déjà dans votre entreprise.')
                : (err?.message || t('features.swimlane.errorSavingAnalysis') || 'Erreur lors de la sauvegarde');
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setAnalysisName('');
            setError('');
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 9999 }}
            tabIndex={-1}
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered">
                <div
                    className="modal-content analysis-save-modal border-0 shadow-lg"
                    style={{ borderRadius: '12px', backgroundColor: '#fff' }}
                >
                    {/* Header */}
                    <div className="modal-header border-0 pb-2">
                        <h5
                            className="modal-title d-flex align-items-center"
                            style={{ color: '#111827' }}
                        >
                            <i className="fa-solid fa-save me-2" style={{ color: '#4CAF50' }}></i>
                            <span style={{ color: '#111827' }}>{t('features.swimlane.saveAnalysis') || 'Sauvegarder l\'analyse'}</span>
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClose}
                            disabled={isLoading}
                            aria-label="Close"
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body pt-0">
                        <div className="mb-3">
                            <label htmlFor="analysisName" className="form-label text-gray-700" style={{ fontSize: '0.9rem' }}>
                                {t('features.swimlane.analysisName') || 'Nom de l\'analyse'}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="analysisName"
                                value={analysisName}
                                onChange={(e) => {
                                    setAnalysisName(e.target.value);
                                    setError('');
                                }}
                                placeholder={t('features.swimlane.enterAnalysisName') || 'Entrez le nom de l\'analyse'}
                                disabled={isLoading}
                                maxLength={100}
                                autoFocus
                                style={{
                                    backgroundColor: '#fff',
                                    color: '#111827',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    padding: '10px 14px',
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSave();
                                    }
                                }}
                            />
                            {error && (
                                <div className="mt-2 small" style={{ color: '#FF6B6B' }}>
                                    <i className="fa-solid fa-exclamation-circle me-1"></i>
                                    {error}
                                </div>
                            )}
                            <small className="form-text mt-1 d-block text-gray-500" style={{ fontSize: '0.85rem' }}>
                                {analysisName.length}/100 {t('words.characters') || 'caractères'}
                            </small>
                        </div>

                        <div className="alert mb-0" style={{
                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                            border: '1px solid rgba(76, 175, 80, 0.25)',
                            color: '#166534',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                        }}>
                            <i className="fa-solid fa-info-circle me-2"></i>
                            {t('features.swimlane.saveAnalysisHint', {
                                section: t('features.swimlane.recentInsights'),
                            }) || `Cette analyse sera accessible depuis « ${t('features.swimlane.recentInsights')} » dans la barre latérale.`}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer border-0 pt-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={isLoading}
                            style={{ borderRadius: '8px' }}
                        >
                            {t('words.cancel') || 'Annuler'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleSave}
                            disabled={isLoading || !analysisName.trim()}
                            style={{ borderRadius: '8px' }}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {t('words.saving') || 'Sauvegarde...'}
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-save me-2"></i>
                                    {t('words.save') || 'Sauvegarder'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

